import React, { useRef } from "react";
import PropTypes from "prop-types";
import { DateRangePicker } from "rsuite";
import { beforeToday } from "rsuite/cjs/DateRangePicker/disabledDateUtils";
import { formatDateRange } from "../../../utils/dateUtils";
import SplitDateRangeFields from "./SplitDateRangeFields";
import deleteIcon from "../../../assets/img/delete.svg";

/**
 * StayDatesSection — multi-range stay date picker (EO-1492).
 *
 * Renders one row per entry in stayDateRanges. Elapsed (isLocked) ranges are
 * shown as read-only text. For the rest: when `splitDateFields` is set (promo
 * edit only, HTT-9780) each range uses separate Start Date / End Date pickers so
 * an already-started range can still have its end extended — the range picker
 * applies one disabled-date rule to both ends and would otherwise lock the whole
 * range. Everywhere else the standard DateRangePicker is used. The "Add another
 * stay date" button always appears while the section is open.
 */
function StayDatesSection({
  stayDateRanges = [],
  onStayDateRangeChange,
  onRemoveStayDateRange,
  onAddStayDateRange,
  onRemoveSection,
  hasErrors = {},
  splitDateFields = false,
}) {
  // Keyed by range id (not array index): ids are large monotonic counters, so an
  // array would balloon into a huge sparse structure. Entries are deleted when a
  // row unmounts so removed ranges leave nothing behind.
  const popupRefs = useRef({});

  return (
    <div className="tc-section condition-form-section">
      {stayDateRanges.map((item, index) => {
        // The last remaining row's delete closes the whole section; any other
        // row's delete just removes that range.
        const handleRemove = () =>
          stayDateRanges.length === 1
            ? onRemoveSection()
            : onRemoveStayDateRange(item.id);
        const removeBtnClass = item.isLocked
          ? "remove-condition-btn remove-condition-btn--hidden"
          : "remove-condition-btn";

        let conditionInput;
        if (item.isLocked) {
          conditionInput = (
            <div className="condition-input-wrapper">
              <div className="stay-date-locked-range">
                {item.dates ? formatDateRange(item.dates) : "—"}
              </div>
            </div>
          );
        } else if (splitDateFields) {
          conditionInput = (
            <SplitDateRangeFields
              startDate={item.dates?.[0] ?? null}
              endDate={item.dates?.[1] ?? null}
              onChange={(value) => onStayDateRangeChange(value, item.id)}
              hasError={!!hasErrors[item.id]}
              showLabels={index === 0}
            />
          );
        } else {
          conditionInput = (
            <div className="condition-input-wrapper">
              <DateRangePicker
                placement="bottom"
                value={item.dates}
                onChange={(value) => onStayDateRangeChange(value, item.id)}
                placeholder="Select date range"
                block
                container={() => popupRefs.current[item.id]}
                renderValue={formatDateRange}
                shouldDisableDate={beforeToday()}
                cleanable={false}
                className={hasErrors[item.id] ? "input-error" : ""}
              />
              <div
                className="position-relative"
                ref={(node) => {
                  if (node) {
                    popupRefs.current[item.id] = node;
                  } else {
                    delete popupRefs.current[item.id];
                  }
                }}
              />
            </div>
          );
        }

        return (
          <div key={item.id} className="condition-row">
            {index === 0 ? (
              <span className="condition-label">Stay Dates</span>
            ) : (
              <span className="condition-label condition-label-empty"></span>
            )}
            {conditionInput}
            <button
              type="button"
              className={removeBtnClass}
              onClick={handleRemove}
              disabled={item.isLocked}
            >
              <img src={deleteIcon} alt="Delete" className="delete-icon" />
            </button>
          </div>
        );
      })}
      {stayDateRanges.length > 0 && (
        <div className="add-stay-date-link">
          <button
            type="button"
            onClick={onAddStayDateRange}
            className="add-another-stay-date-btn"
          >
            Add another stay date
          </button>
        </div>
      )}
    </div>
  );
}

StayDatesSection.propTypes = {
  stayDateRanges: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      dates: PropTypes.arrayOf(PropTypes.instanceOf(Date)),
      isLocked: PropTypes.bool,
    })
  ),
  onStayDateRangeChange: PropTypes.func.isRequired,
  onRemoveStayDateRange: PropTypes.func.isRequired,
  onAddStayDateRange: PropTypes.func.isRequired,
  onRemoveSection: PropTypes.func.isRequired,
  hasErrors: PropTypes.object,
  splitDateFields: PropTypes.bool,
};

export default StayDatesSection;
